import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageBanner from "@/components/PageBanner";
import Reveal from "@/components/Reveal";
import { STORY_IMG, LIFESTYLE_IMG, VALUES, SITE } from "@/data/site";

const STATS = [
  { value: "100%", label: "Organic apples" },
  { value: "8+", label: "Years of craft" },
  { value: "0", label: "Additives or fillers" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageBanner
        eyebrow="About Us"
        title="A family of makers devoted to honest wellness"
        subtitle={`${SITE.legal} was founded on a simple promise — to bottle nature with intention, and share it with families who care about what they put into their bodies.`}
        testId="about-banner"
      />

      {/* Story editorial */}
      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-line">
              <img src={LIFESTYLE_IMG} alt="Wellness lifestyle" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Our beginning</p>
            <h2 className="mt-3 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              From a home kitchen to your daily ritual
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink/65">
              What started as small jars of vinegar fermenting on a windowsill grew into a promise: to
              make wellness that's pure, honest and never over-processed. We were tired of labels we
              couldn't pronounce, so we made something we'd be proud to give our own family.
            </p>
            <p className="mt-4 text-base leading-relaxed text-ink/65">
              Today, every bottle of Medina Lia still carries that same intention — real ingredients,
              patient fermentation, and care in every step.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-3 gap-4 border-y border-line py-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="text-center">
                <p className="font-heading text-4xl text-forest md:text-6xl">{s.value}</p>
                <p className="mt-2 text-xs uppercase tracking-wider text-ink/55 md:text-sm">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-forest py-16 text-cream md:py-24" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber">What we stand for</p>
            <h2 className="mt-3 max-w-xl font-heading text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl">
              Principles we never compromise
            </h2>
          </Reveal>
          <div className="mt-14 space-y-10">
            {VALUES.map((v, i) => (
              <Reveal key={v.number} delay={i * 0.08}>
                <div className="grid items-start gap-4 border-t border-cream/20 pt-8 md:grid-cols-12">
                  <span className="font-heading text-3xl text-amber md:col-span-2">{v.number}</span>
                  <h3 className="font-heading text-2xl md:col-span-4">{v.title}</h3>
                  <p className="text-base leading-relaxed text-cream/70 md:col-span-6">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Image + CTA */}
      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Our promise</p>
            <h2 className="mt-3 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              Wellness you can trust, in every bottle
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink/65">
              We believe good things take time. That's why we never rush fermentation and never cut
              corners. When you choose Medina Lia, you're choosing transparency, quality and a company
              that genuinely cares about your wellbeing.
            </p>
            <Link
              to="/contact"
              data-testid="about-contact-btn"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-forest"
            >
              Get in touch
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </Link>
          </Reveal>
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-line">
              <img src={STORY_IMG} alt="Organic apples basket" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
